import { useState, useCallback } from "react";
import { Container, Form, Button } from 'react-bootstrap';
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import Tooltip from "@mui/material/Tooltip";

import { theme } from "../../../../theme";
import ModalTemplateWithForm from "../../../../components/modal/modalTemplateWithForm";
import BtnSpinner from "../../../../components/btnSpinner";
import { IconButton } from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { SetFollowupReminderDate } from "../service";

const SetFollowupReminder = ({ ftm_id, ftm_notes_followup_reminder_date, onSave }) => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm();

    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const defaultDate = ftm_notes_followup_reminder_date
        ? new Date(ftm_notes_followup_reminder_date).toISOString().split('T')[0]
        : "";

    const getInputStyle = (hasError) => ({
        backgroundColor: '#fff',
        paddingLeft: '10px',
        paddingRight: '10px',
        fontSize: '15px',
        ...(hasError && {
            borderColor: '#d32f2f',
            borderStyle: 'solid',
            borderWidth: '1px'
        })
    });

    const toggleModal = useCallback(() => setShow(prev => !prev), []);

    const handleReminderDateSubmit = (e) => {
        handleSubmit(onSubmit)(e);
    };

    const onSubmit = useCallback(async (data) => {
        if (!data.followup_reminder_date) return;

        setLoading(true);

        try {
            const timestamp = new Date(data.followup_reminder_date).getTime();

            const response = await SetFollowupReminderDate(
                ftm_id,
                timestamp
            );

            if (response?.status === "Success") {
                toast.success(response.message);
                reset();
                toggleModal();

                // Call parent refresh callback
                onSave?.();
            } else {
                toast.error(response?.message || "Failed to set reminder");
            }
        } catch (error) {
            console.error("Error setting followup reminder date:", error);
            toast.error("An error occurred while setting the reminder");
        } finally {
            setLoading(false);
        }
    }, [ftm_id, reset, toggleModal, onSave]);

    return (
        <>
            <Tooltip title="Set Followup Reminder" arrow>
                <IconButton
                    variant={theme.set_expired_color}
                    onClick={toggleModal}
                    aria-label="Set Followup Reminder"
                >
                    <CalendarTodayIcon />
                </IconButton>
            </Tooltip>

            <ModalTemplateWithForm
                show={show}
                closeFun={toggleModal}
                title="Set Followup Reminder"
                body={
                    <Container>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="followup_reminder_date">REMINDER DATE</Form.Label>
                            <Form.Control
                                id="followup_reminder_date"
                                type="date"
                                error={errors.followup_reminder_date}
                                style={getInputStyle(errors.followup_reminder_date)}
                                defaultValue={defaultDate}
                                min={new Date().toISOString().split("T")[0]}
                                {...register("followup_reminder_date", {
                                    required: "Date is required"
                                })}
                                aria-invalid={errors.followup_reminder_date ? 'true' : 'false'}
                            />
                            {errors.followup_reminder_date && (
                                <Form.Text className="text-danger">
                                    {errors.followup_reminder_date.message}
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Container>
                }
                footer={
                    <>
                        <Button
                            variant={theme.close_btn}
                            size="sm"
                            disabled={loading}
                            onClick={toggleModal}
                        >
                            {loading ? <BtnSpinner /> : "CLOSE"}
                        </Button>
                        <Button
                            variant={theme.action_btn}
                            type="button"
                            size="sm"
                            disabled={loading}
                            onClick={handleReminderDateSubmit}
                        >
                            {loading ? <BtnSpinner /> : "SAVE"}
                        </Button>
                    </>
                }
            />
        </>
    );
};

export default SetFollowupReminder;